import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/core/notifications/notify';
import ConfirmationModal from '@/components/ConfirmationModal';
import {
  FileText,
  Plus,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Eye,
  Edit3,
  Trash2,
  X,
  Shield,
  Globe,
  AlertTriangle,
  Clock,
  CheckCircle,
  BookOpen,
  Users,
  Database,
  Scale,
} from 'lucide-react';

// --- Types ---

interface RopaRegister {
  id: number;
  company_id: number;
  register_name: string;
  version: number;
  status: string;
  approved_by: number | null;
  approved_at: string | null;
  review_due_at: string | null;
  created_by: number;
  created_by_name?: string;
  activity_count?: number;
  created_at?: string;
  updated_at?: string;
}

interface ProcessingActivity {
  id: number;
  register_id: number;
  company_id: number;
  activity_name: string;
  purpose_of_processing: string;
  lawful_basis: string;
  lawful_basis_detail?: string;
  categories_of_data_subjects: string | string[];
  categories_of_personal_data: string | string[];
  special_categories: boolean | number;
  special_categories_detail?: string;
  recipients?: string;
  international_transfers: boolean | number;
  transfer_safeguards?: string;
  transfer_countries?: string;
  retention_period?: string;
  retention_justification?: string;
  technical_measures?: string;
  organisational_measures?: string;
  data_source?: string;
  automated_decision_making: boolean | number;
  automated_decision_detail?: string;
  dpia_required: boolean | number;
  dpia_reference?: string;
  systems_used?: string;
  data_processor?: string;
  processor_contract_reference?: string;
  owner_user_id?: number;
  status: string;
  last_reviewed_at?: string;
  created_at?: string;
}

interface DashboardStats {
  registers_count: number;
  latest_register: any;
  stats: {
    total_activities: number;
    by_lawful_basis: Record<string, number>;
    special_categories_count: number;
    international_transfers_count: number;
    dpia_required_count: number;
    review_overdue: number;
  };
}

// --- Helpers ---

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const LAWFUL_BASES = [
  { value: 'consent', label: 'Consent' },
  { value: 'contract', label: 'Contract' },
  { value: 'legal_obligation', label: 'Legal Obligation' },
  { value: 'vital_interests', label: 'Vital Interests' },
  { value: 'public_task', label: 'Public Task' },
  { value: 'legitimate_interests', label: 'Legitimate Interests' },
];

const STATUS_FLOW: Record<string, string[]> = {
  draft: ['under_review'],
  under_review: ['approved', 'draft'],
  approved: ['published', 'under_review'],
  published: ['under_review'],
};

const statusColor = (s: string) => {
  switch (s) {
    case 'draft': return 'bg-gray-100 text-gray-700';
    case 'under_review': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'published': return 'bg-blue-100 text-blue-800';
    case 'active': return 'bg-green-100 text-green-700';
    case 'retired': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const statusLabel = (s: string) => s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) ?? '';

const formatDate = (d: string | null | undefined) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString();
};

const parseJsonField = (val: any): string[] => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try { return JSON.parse(val); } catch { return []; }
};

const toBool = (v: any): boolean => v === true || v === 1 || v === '1';

// --- Component ---

export default function RopaRegister() {
  const { hasPermission } = useAuth();

  const [dashStats, setDashStats] = useState<DashboardStats | null>(null);
  const [registers, setRegisters] = useState<RopaRegister[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Expanded register + its activities
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [activities, setActivities] = useState<ProcessingActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Create Register Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRegName, setNewRegName] = useState('');
  const [creating, setCreating] = useState(false);

  // Activity Modal (create/edit)
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<ProcessingActivity | null>(null);
  const [activityRegisterId, setActivityRegisterId] = useState<number | null>(null);
  const [activityForm, setActivityForm] = useState<any>({});
  const [savingActivity, setSavingActivity] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: number; name: string } | null>(null);

  // --- Data Fetching ---

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch('/api/compliance/ropa/dashboard', { headers: authHeaders() });
      if (!res.ok) { setDashStats(null); return; }
      const json = await res.json();
      setDashStats(json.data ?? null);
    } catch (err) {
      console.error('Error fetching ROPA dashboard', err);
      setDashStats(null);
    }
  }, []);

  const fetchRegisters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/compliance/ropa/registers', { headers: authHeaders() });
      if (!res.ok) {
        setRegisters([]);
        if (res.status !== 404) notify.error('Failed to load registers');
        return;
      }
      const json = await res.json();
      const list = json?.data ?? json;
      setRegisters(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching registers', err);
      setRegisters([]);
      notify.error('Failed to load registers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActivities = useCallback(async (registerId: number) => {
    setActivitiesLoading(true);
    try {
      const res = await fetch(`/api/compliance/ropa/registers/${registerId}`, { headers: authHeaders() });
      if (!res.ok) { setActivities([]); return; }
      const json = await res.json();
      const list = json?.data?.activities ?? json?.activities;
      setActivities(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching activities', err);
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); fetchRegisters(); }, [fetchDashboard, fetchRegisters]);

  useEffect(() => {
    if (expandedId) fetchActivities(expandedId);
    else setActivities([]);
  }, [expandedId, fetchActivities]);

  // --- Register Actions ---

  const handleCreateRegister = async () => {
    if (!newRegName.trim()) { notify.warning('Please enter a register name'); return; }
    setCreating(true);
    try {
      const res = await fetch('/api/compliance/ropa/registers', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: newRegName.trim() }),
      });
      if (res.ok) {
        notify.success('Register created');
        setShowCreateModal(false);
        setNewRegName('');
        fetchRegisters();
        fetchDashboard();
      } else {
        const json = await res.json();
        notify.error(json.error || 'Failed to create register');
      }
    } catch (err) {
      notify.error('Failed to create register');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (registerId: number, newStatus: string) => {
    try {
      const res = await fetch(`/api/compliance/ropa/registers/${registerId}/status`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        notify.success(`Status updated to ${statusLabel(newStatus)}`);
        fetchRegisters();
        fetchDashboard();
      } else {
        notify.error('Failed to update status');
      }
    } catch (err) {
      notify.error('Failed to update status');
    }
  };

  const handleExport = async (registerId: number, format: string) => {
    try {
      const res = await fetch(`/api/compliance/ropa/registers/${registerId}/export?format=${format}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'X-CSRF-Token': getCsrfToken() },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ropa-register-${registerId}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
        notify.success(`Exported as ${format.toUpperCase()}`);
      } else {
        notify.error('Export failed');
      }
    } catch (err) {
      notify.error('Export failed');
    }
  };

  // --- Activity Actions ---

  const resetActivityForm = () => {
    setActivityForm({
      activity_name: '', purpose_of_processing: '', lawful_basis: 'legitimate_interests',
      categories_of_data_subjects: '', categories_of_personal_data: '',
      special_categories: false, special_categories_detail: '',
      international_transfers: false, transfer_countries: '', transfer_safeguards: '',
      retention_period: '', automated_decision_making: false, automated_decision_detail: '',
      dpia_required: false, data_processor: '', technical_measures: '',
    });
  };

  const openCreateActivity = (registerId: number) => {
    setEditingActivity(null);
    setActivityRegisterId(registerId);
    resetActivityForm();
    setShowActivityModal(true);
  };

  const openEditActivity = (activity: ProcessingActivity) => {
    setEditingActivity(activity);
    setActivityRegisterId(activity.register_id);
    setActivityForm({
      activity_name: activity.activity_name || '',
      purpose_of_processing: activity.purpose_of_processing || '',
      lawful_basis: activity.lawful_basis || 'legitimate_interests',
      categories_of_data_subjects: parseJsonField(activity.categories_of_data_subjects).join(', '),
      categories_of_personal_data: parseJsonField(activity.categories_of_personal_data).join(', '),
      special_categories: toBool(activity.special_categories),
      special_categories_detail: activity.special_categories_detail || '',
      international_transfers: toBool(activity.international_transfers),
      transfer_countries: parseJsonField(activity.transfer_countries).join(', '),
      transfer_safeguards: activity.transfer_safeguards || '',
      retention_period: activity.retention_period || '',
      automated_decision_making: toBool(activity.automated_decision_making),
      automated_decision_detail: activity.automated_decision_detail || '',
      dpia_required: toBool(activity.dpia_required),
      data_processor: activity.data_processor || '',
      technical_measures: activity.technical_measures ? (Array.isArray(JSON.parse(activity.technical_measures || '[]')) ? JSON.parse(activity.technical_measures).join(', ') : activity.technical_measures) : '',
    });
    setShowActivityModal(true);
  };

  const handleSaveActivity = async () => {
    if (!activityForm.activity_name?.trim()) { notify.warning('Activity name is required'); return; }
    setSavingActivity(true);

    const payload: any = {
      activity_name: activityForm.activity_name.trim(),
      purpose_of_processing: activityForm.purpose_of_processing,
      lawful_basis: activityForm.lawful_basis,
      categories_of_data_subjects: activityForm.categories_of_data_subjects.split(',').map((s: string) => s.trim()).filter(Boolean),
      categories_of_personal_data: activityForm.categories_of_personal_data.split(',').map((s: string) => s.trim()).filter(Boolean),
      special_categories: activityForm.special_categories,
      special_categories_detail: activityForm.special_categories_detail,
      international_transfers: activityForm.international_transfers,
      transfer_countries: activityForm.international_transfers ? activityForm.transfer_countries.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      transfer_safeguards: activityForm.transfer_safeguards,
      retention_period: activityForm.retention_period,
      automated_decision_making: activityForm.automated_decision_making,
      automated_decision_detail: activityForm.automated_decision_detail,
      dpia_required: activityForm.dpia_required,
      data_processor: activityForm.data_processor,
      technical_measures: activityForm.technical_measures.split(',').map((s: string) => s.trim()).filter(Boolean),
    };

    try {
      let res;
      if (editingActivity) {
        res = await fetch(`/api/compliance/ropa/activities/${editingActivity.id}`, {
          method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`/api/compliance/ropa/registers/${activityRegisterId}/activities`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
        });
      }
      if (res.ok) {
        notify.success(editingActivity ? 'Activity updated' : 'Activity created');
        setShowActivityModal(false);
        if (expandedId) fetchActivities(expandedId);
        fetchDashboard();
        fetchRegisters();
      } else {
        const json = await res.json();
        notify.error(json.error || 'Failed to save activity');
      }
    } catch (err) {
      notify.error('Failed to save activity');
    } finally {
      setSavingActivity(false);
    }
  };

  const handleDeleteActivity = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/compliance/ropa/activities/${deleteTarget.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (res.ok) {
        notify.success('Activity deleted');
        if (expandedId) fetchActivities(expandedId);
        fetchDashboard();
        fetchRegisters();
      } else {
        notify.error('Failed to delete activity');
      }
    } catch (err) {
      notify.error('Failed to delete activity');
    } finally {
      setDeleteTarget(null);
    }
  };

  // --- Filtered Registers ---

  const filteredRegisters = registers.filter(r =>
    !searchTerm || r.register_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const s = dashStats?.stats;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ROPA Register</h1>
            <p className="text-gray-500">Record of Processing Activities (GDPR Article 30)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { fetchRegisters(); fetchDashboard(); }} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => { setNewRegName(''); setShowCreateModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
            <Plus className="w-4 h-4" /> New Register
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Registers</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{dashStats?.registers_count ?? registers.length}</p>
            </div>
            <div className="p-2 bg-purple-50 rounded-lg"><FileText className="w-5 h-5 text-purple-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Activities</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{s?.total_activities ?? 0}</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg"><Database className="w-5 h-5 text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Special Categories</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{s?.special_categories_count ?? 0}</p>
            </div>
            <div className="p-2 bg-red-50 rounded-lg"><Shield className="w-5 h-5 text-red-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Int'l Transfers</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{s?.international_transfers_count ?? 0}</p>
            </div>
            <div className="p-2 bg-green-50 rounded-lg"><Globe className="w-5 h-5 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">DPIA Required</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{s?.dpia_required_count ?? 0}</p>
            </div>
            <div className="p-2 bg-yellow-50 rounded-lg"><AlertTriangle className="w-5 h-5 text-yellow-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Overdue Reviews</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{s?.review_overdue ?? 0}</p>
            </div>
            <div className="p-2 bg-orange-50 rounded-lg"><Clock className="w-5 h-5 text-orange-600" /></div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search registers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
        </div>
      </div>

      {/* Registers List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-48 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredRegisters.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 bg-white rounded-xl border border-gray-200 text-gray-400">
            <BookOpen className="w-12 h-12 mb-3" />
            <p className="text-lg font-medium">No registers found</p>
            <p className="text-sm">Create your first ROPA register to get started</p>
          </div>
        ) : (
          filteredRegisters.map(reg => (
            <div key={reg.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Register Row */}
              <div className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(expandedId === reg.id ? null : reg.id)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <button className="p-1 text-gray-400">
                    {expandedId === reg.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{reg.register_name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>v{reg.version}</span>
                      <span>{reg.activity_count ?? 0} activities</span>
                      {reg.created_by_name && <span>by {reg.created_by_name}</span>}
                      <span>Created {formatDate(reg.created_at)}</span>
                      {reg.review_due_at && <span>Review due {formatDate(reg.review_due_at)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(reg.status)}`}>
                    {statusLabel(reg.status)}
                  </span>
                  {/* Status workflow buttons */}
                  {STATUS_FLOW[reg.status]?.map(next => (
                    <button key={next} onClick={e => { e.stopPropagation(); handleStatusChange(reg.id, next); }}
                      className="px-2.5 py-1 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 whitespace-nowrap">
                      {statusLabel(next)}
                    </button>
                  ))}
                  <button onClick={e => { e.stopPropagation(); handleExport(reg.id, 'json'); }} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Export JSON">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); handleExport(reg.id, 'csv'); }} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Export CSV">
                    <FileText className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Activities */}
              {expandedId === reg.id && (
                <div className="border-t border-gray-200 bg-gray-50 px-5 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Processing Activities</h4>
                    <button onClick={() => openCreateActivity(reg.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs">
                      <Plus className="w-3.5 h-3.5" /> Add Activity
                    </button>
                  </div>

                  {activitiesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    </div>
                  ) : activities.filter(a => a.status !== 'retired').length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No activities yet. Add your first processing activity.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full text-sm bg-white">
                        <thead className="bg-gray-100 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Activity</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Lawful Basis</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Special Cat.</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Int'l Transfer</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">DPIA</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Retention</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Status</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {activities.filter(a => a.status !== 'retired').map(act => (
                            <tr key={act.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2.5">
                                <p className="font-medium text-gray-900 text-xs">{act.activity_name}</p>
                                <p className="text-xs text-gray-500 truncate max-w-[200px]">{act.purpose_of_processing}</p>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                                  {LAWFUL_BASES.find(b => b.value === act.lawful_basis)?.label || act.lawful_basis}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                {toBool(act.special_categories) ? <Shield className="w-4 h-4 text-red-500" /> : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                {toBool(act.international_transfers) ? <Globe className="w-4 h-4 text-blue-500" /> : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                {toBool(act.dpia_required) ? <AlertTriangle className="w-4 h-4 text-yellow-500" /> : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-3 py-2.5 text-xs text-gray-600">{act.retention_period || '-'}</td>
                              <td className="px-3 py-2.5">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(act.status)}`}>
                                  {statusLabel(act.status)}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-1">
                                  <button onClick={() => openEditActivity(act)} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit">
                                    <Edit3 className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => setDeleteTarget({ type: 'activity', id: act.id, name: act.activity_name })} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Register Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">Create New Register</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Register Name</label>
                <input type="text" value={newRegName} onChange={e => setNewRegName(e.target.value)} placeholder="e.g. Main ROPA Register 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button onClick={handleCreateRegister} disabled={creating} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm">
                  {creating ? 'Creating...' : 'Create Register'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Modal (Create/Edit) */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowActivityModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">{editingActivity ? 'Edit Activity' : 'Add Processing Activity'}</h2>
              <button onClick={() => setShowActivityModal(false)} className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-130px)] p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity Name *</label>
                  <input type="text" value={activityForm.activity_name} onChange={e => setActivityForm({ ...activityForm, activity_name: e.target.value })}
                    placeholder="e.g. Customer onboarding"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purpose of Processing</label>
                  <textarea value={activityForm.purpose_of_processing} onChange={e => setActivityForm({ ...activityForm, purpose_of_processing: e.target.value })}
                    rows={2} placeholder="Describe the purpose of this processing activity"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lawful Basis</label>
                  <select value={activityForm.lawful_basis} onChange={e => setActivityForm({ ...activityForm, lawful_basis: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500">
                    {LAWFUL_BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Retention Period</label>
                  <input type="text" value={activityForm.retention_period} onChange={e => setActivityForm({ ...activityForm, retention_period: e.target.value })}
                    placeholder="e.g. 7 years"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Subjects (comma-separated)</label>
                  <input type="text" value={activityForm.categories_of_data_subjects} onChange={e => setActivityForm({ ...activityForm, categories_of_data_subjects: e.target.value })}
                    placeholder="e.g. Customers, Employees"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Personal Data Categories (comma-separated)</label>
                  <input type="text" value={activityForm.categories_of_personal_data} onChange={e => setActivityForm({ ...activityForm, categories_of_personal_data: e.target.value })}
                    placeholder="e.g. Name, Email, Address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Processor</label>
                  <input type="text" value={activityForm.data_processor} onChange={e => setActivityForm({ ...activityForm, data_processor: e.target.value })}
                    placeholder="e.g. AWS, Salesforce"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Technical Measures (comma-separated)</label>
                  <input type="text" value={activityForm.technical_measures} onChange={e => setActivityForm({ ...activityForm, technical_measures: e.target.value })}
                    placeholder="e.g. Encryption, Access controls"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>

              {/* Toggle fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  <input type="checkbox" checked={activityForm.special_categories} onChange={e => setActivityForm({ ...activityForm, special_categories: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">Special Categories</label>
                    <p className="text-xs text-gray-500">Processes special category data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  <input type="checkbox" checked={activityForm.international_transfers} onChange={e => setActivityForm({ ...activityForm, international_transfers: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">International Transfers</label>
                    <p className="text-xs text-gray-500">Data transferred outside EEA</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  <input type="checkbox" checked={activityForm.automated_decision_making} onChange={e => setActivityForm({ ...activityForm, automated_decision_making: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">Automated Decisions</label>
                    <p className="text-xs text-gray-500">Includes automated decision-making</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  <input type="checkbox" checked={activityForm.dpia_required} onChange={e => setActivityForm({ ...activityForm, dpia_required: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">DPIA Required</label>
                    <p className="text-xs text-gray-500">Data Protection Impact Assessment needed</p>
                  </div>
                </div>
              </div>

              {/* Conditional fields */}
              {activityForm.special_categories && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Categories Justification</label>
                  <textarea value={activityForm.special_categories_detail} onChange={e => setActivityForm({ ...activityForm, special_categories_detail: e.target.value })}
                    rows={2} placeholder="Justify processing of special category data"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
              )}
              {activityForm.international_transfers && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Countries (comma-separated)</label>
                    <input type="text" value={activityForm.transfer_countries} onChange={e => setActivityForm({ ...activityForm, transfer_countries: e.target.value })}
                      placeholder="e.g. US, Canada"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Safeguards</label>
                    <input type="text" value={activityForm.transfer_safeguards} onChange={e => setActivityForm({ ...activityForm, transfer_safeguards: e.target.value })}
                      placeholder="e.g. Standard Contractual Clauses"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                  </div>
                </div>
              )}
              {activityForm.automated_decision_making && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Automated Decision Details</label>
                  <textarea value={activityForm.automated_decision_detail} onChange={e => setActivityForm({ ...activityForm, automated_decision_detail: e.target.value })}
                    rows={2} placeholder="Describe the automated decision-making process"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setShowActivityModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={handleSaveActivity} disabled={savingActivity} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm">
                {savingActivity ? 'Saving...' : editingActivity ? 'Update Activity' : 'Add Activity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteActivity}
        title="Delete Activity"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This will mark the activity as retired.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
}
